<template>
  <div class="add-discovery-campaign-general-information">
    <ElForm
      ref="campaignModel"
      :model="campaignModel"
      :rules="campaignRules"
      label-position="top"
      @submit.native.prevent="submitForm"
    >
      <ElFormItem prop="name" :label="$t('ADVERTISEMENT.CAMPAIGNS.NAME')">
        <ElInput v-model="campaignModel.name" name="name" required />
      </ElFormItem>

      <ElFormItem
        prop="packageName"
        :label="$t('ADVERTISEMENT.CAMPAIGNS.PACKAGE_NAME')"
      >
        <AlternativeSelect
          v-model="campaignModel.packageName"
          key-prop="packageName"
          :items="apps"
          label-prop="packageName"
          value-prop="packageName"
        />
      </ElFormItem>

      <ElFormItem prop="paused" :label="$t('ADVERTISEMENT.CAMPAIGNS.STATUS')">
        <AlternativeSelect
          v-model="campaignModel.paused"
          key-prop="value"
          :items="pausedOptions"
        />
      </ElFormItem>

      <ElFormItem :label="$t('ADVERTISEMENT.CAMPAIGNS.START_DATE')">
        <UiDatePickerEndFuture
          :default-date="currentDate"
          @select-date="setStartDate"
        />
      </ElFormItem>

      <FormItemWithHint
        :label="$t('ADVERTISEMENT.CAMPAIGNS.END_DATE')"
        :hint="$t('HINTS.DISCOVERY.END_DATE')"
      >
        <UiDatePickerEndFuture
          :default-date="nextMonthDate"
          @select-date="setEndDate"
        />
      </FormItemWithHint>

      <ElFormItem prop="hasTracker">
        <ElCheckbox v-model="campaignModel.hasTracker">
          {{ $t('ADVERTISEMENT.CAMPAIGNS.CHOOSE_TO_HAVE_TRACKER') }}
        </ElCheckbox>
      </ElFormItem>

      <ElCollapseTransition>
        <div v-show="campaignModel.hasTracker">
          <FormItemWithHint
            prop="impressionTrackerLink"
            :label="
              $t('ADVERTISEMENT.CAMPAIGNS.DISCOVERY.IMPRESSION_TRACKER_LINK')
            "
            :hint="$t('HINTS.DISCOVERY.TRACKER_EXAMPLES')"
          >
            <ElInput
              v-model="campaignModel.impressionTrackerLink"
              type="url"
              dir="ltr"
            />
            <p class="el-form-item__help">
              <i class="el-icon-warning" />
              {{
                $t('ADVERTISEMENT.NEW_CAMPAIGN_DISCOVERY.AVAILABLE_TRACKERS')
              }}
            </p>
          </FormItemWithHint>
        </div>
      </ElCollapseTransition>
    </ElForm>
    <ElButton
      type="primary"
      native-type="submit"
      size="large"
      :loading="isLoading"
      @click.native.prevent="submitForm"
    >
      {{ $t('ADVERTISEMENT.NEW_CAMPAIGN_DISCOVERY.NEXT_STEP_CITY_AND_PRICE') }}
    </ElButton>
  </div>
</template>

<script>
import UiDatePickerEndFuture from '@@/common/UiDatePickerEndFuture';
import AlternativeSelect from '@@/common/AlternativeSelect';
import FormItemWithHint from '@@/common/FormItemWithHint';

import { dateFormat } from '@/utils/date';

import form from '@/mixins/form';
import requiredWithMessage from '@/utils/validation-rules/required';

export default {
  name: 'AddDiscoveryCampaignGeneralInformation',
  components: {
    UiDatePickerEndFuture,
    AlternativeSelect,
    FormItemWithHint,
  },
  mixins: [form],
  props: {
    resetController: {
      type: Object,
      required: true,
    },
    apps: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      campaignModel: {
        name: '',
        packageName: '',
        startDate: '',
        endDate: '',
        paused: false,
        hasTracker: false,
        impressionTrackerLink: '',
      },
    };
  },
  computed: {
    pausedOptions() {
      return [
        {
          value: false,
          label: this.$t('ADVERTISEMENT.CAMPAIGNS.ACTIVE'),
        },
        {
          value: true,
          label: this.$t('ADVERTISEMENT.CAMPAIGNS.PAUSED'),
        },
      ];
    },
    currentDate() {
      return Date.now();
    },

    nextMonthDate() {
      const date = new Date();
      return date.setMonth(date.getMonth() + 1);
    },

    campaignRules() {
      return {
        name: {
          ...requiredWithMessage(this.$t('VALIDATION_REQUIRED_ERR')),
          type: 'string',
        },
        packageName: {
          ...requiredWithMessage(this.$t('VALIDATION_REQUIRED_ERR')),
          type: 'string',
        },
        startDate: {
          ...requiredWithMessage(this.$t('VALIDATION_REQUIRED_ERR')),
          type: 'date',
        },
        endDate: {
          ...requiredWithMessage(this.$t('VALIDATION_REQUIRED_ERR')),
          type: 'date',
        },
        paused: {
          ...requiredWithMessage(this.$t('VALIDATION_REQUIRED_ERR')),
          type: 'boolean',
        },
        impressionTrackerLink: {
          type: 'url',
          message: this.$t('VALIDATION.URL_IS_INVALID'),
          trigger: 'blur',
        },
      };
    },
  },
  watch: {
    'campaignModel.hasTracker'() {
      if (!this.campaignModel.hasTracker) {
        this.campaignModel.impressionTrackerLink = '';
      }
    },
  },
  created() {
    this.resetController.onReset(this.reset);
  },
  beforeDestroy() {
    this.resetController.removeOnReset(this.reset);
  },
  methods: {
    submitForm() {
      if (!this.isValid('campaignModel')) {
        return false;
      }
      this.$emit('submit', this.campaignModel);
    },

    setStartDate(selectedDate) {
      this.$set(
        this.campaignModel,
        'startDate',
        dateFormat()('YYYY-MM-DDT00:00:00')(selectedDate),
      );
    },

    setEndDate(selectedDate) {
      this.$set(
        this.campaignModel,
        'endDate',
        dateFormat()('YYYY-MM-DDT23:59:59')(selectedDate),
      );
    },

    reset() {
      this.resetForm('campaignModel');
    },
  },
};
</script>
